import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Check, Sparkles, Rocket, Building } from 'lucide-react';
import { getPlanPricing } from '../../lib/pricing';

const Step3PlanSelection = ({ formData, loading, onNext, onPrevious }) => {
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState(formData.plan_code || 'STARTER');
  const [billingCycle, setBillingCycle] = useState(formData.billing_cycle || 'monthly');

  const plans = [
    { code: 'STARTER', nameKey: 'starter', icon: Sparkles, employee_limit: 50, featureKeys: ['upTo50Employees', 'biometricAttendance', 'absenceManagement', 'basicPlanning', 'standardReports', 'emailSupport'], highlight: false },
    { code: 'PROFESSIONAL', nameKey: 'professional', icon: Rocket, employee_limit: 200, featureKeys: ['upTo200Employees', 'allFeatures', 'aiPayrollRecruitment', 'prioritySupport', 'trainingIncluded', 'advancedIntegrations', 'apiAccess'], highlight: true },
    { code: 'ENTERPRISE', nameKey: 'enterprise', icon: Building, employee_limit: null, featureKeys: ['unlimitedEmployees', 'allProFeatures', 'onPremise', 'dedicatedSupport247', 'guaranteedSLA', 'fullCustomization', 'onSiteTraining'], highlight: false },
  ];

  const handleSubmit = (e) => { e.preventDefault(); onNext({ plan_code: selectedPlan, billing_cycle: billingCycle }); };

  return (
    <div className="p-8">
      <div className="mb-7">
        <h2 className="text-xl font-semibold mb-1" style={{ color: "var(--lm-ink)", letterSpacing: "-0.4px" }}>{t('registration.step3.title')}</h2>
        <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>{t('registration.step3.subtitle')}</p>
      </div>

      {/* Billing toggle */}
      <div className="flex justify-center mb-7">
        <div className="inline-flex p-1 rounded-full" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid #23252a" }}>
          {[
            { key: 'monthly', label: t('registration.step3.monthly') },
            { key: 'annual', label: t('registration.step3.annual') },
          ].map(opt => (
            <button key={opt.key} type="button" onClick={() => setBillingCycle(opt.key)}
              className="px-5 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2"
              style={{
                backgroundColor: billingCycle === opt.key ? "var(--lm-s3)" : "transparent",
                color: billingCycle === opt.key ? "var(--lm-ink)" : "var(--lm-ink-2)",
                border: billingCycle === opt.key ? "1px solid #34343a" : "1px solid transparent",
              }}>
              {opt.label}
              {opt.key === 'annual' && (
                <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "rgba(39,166,68,0.15)", color: "#27a644" }}>-17%</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid md:grid-cols-3 gap-4 mb-7">
          {plans.map(plan => {
            const Icon = plan.icon;
            const isSelected = selectedPlan === plan.code;
            const pricing = getPlanPricing(plan.code, formData.country_code);
            return (
              <div key={plan.code} onClick={() => setSelectedPlan(plan.code)}
                className="relative p-5 rounded-xl cursor-pointer transition-all flex flex-col"
                style={{
                  backgroundColor: isSelected ? "rgba(94,106,210,0.08)" : "var(--lm-s2)",
                  border: `1px solid ${isSelected ? "#5e6ad2" : plan.highlight ? "var(--lm-border-2)" : "var(--lm-border)"}`,
                }}>
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="text-xs font-medium px-3 py-0.5 rounded-full" style={{ backgroundColor: "#5e6ad2", color: "#fff" }}>
                      {t('registration.step3.recommended')}
                    </span>
                  </div>
                )}

                <div className="text-center mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3"
                    style={{ backgroundColor: isSelected ? "rgba(94,106,210,0.2)" : "var(--lm-s3)", border: "1px solid #23252a" }}>
                    <Icon className="w-5 h-5" style={{ color: isSelected ? "#828fff" : "var(--lm-ink-3)" }} />
                  </div>
                  <h3 className="font-semibold text-sm mb-0.5" style={{ color: "var(--lm-ink)" }}>{t(`registration.step3.plans.${plan.nameKey}.name`)}</h3>
                  <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>{t(`registration.step3.plans.${plan.nameKey}.description`)}</p>
                </div>

                <div className="text-center mb-4">
                  {billingCycle === 'monthly' ? (
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-2xl font-semibold" style={{ color: "var(--lm-ink)", letterSpacing: "-0.5px" }}>{pricing.monthly}</span>
                      <span className="text-xs" style={{ color: "var(--lm-ink-2)" }}>{pricing.symbol}/{t('registration.step3.month')}</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-2xl font-semibold" style={{ color: "var(--lm-ink)", letterSpacing: "-0.5px" }}>{pricing.annual}</span>
                        <span className="text-xs" style={{ color: "var(--lm-ink-2)" }}>{pricing.symbol}/{t('registration.step3.year')}</span>
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: "#27a644" }}>
                        {t('registration.step3.saveCurrency', { amount: pricing.annualSavings, currency: pricing.symbol })}
                      </p>
                    </>
                  )}
                  <p className="text-xs mt-1" style={{ color: "var(--lm-ink-3)" }}>
                    {plan.employee_limit ? t('registration.step3.maxEmployees', { count: plan.employee_limit }) : t('registration.step3.unlimitedEmployees')}
                  </p>
                </div>

                <ul className="space-y-1.5 mb-4 flex-1">
                  {plan.featureKeys.map((k, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs" style={{ color: "#d0d6e0" }}>
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "#27a644" }} />
                      {t(`registration.step3.features.${k}`)}
                    </li>
                  ))}
                </ul>

                <button type="button" onClick={() => setSelectedPlan(plan.code)}
                  className="w-full py-2 rounded-lg text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: isSelected ? "#5e6ad2" : "var(--lm-s3)",
                    color: isSelected ? "#fff" : "var(--lm-ink)",
                    border: `1px solid ${isSelected ? "#5e6ad2" : "var(--lm-border)"}`,
                  }}>
                  {isSelected ? t('registration.step3.selected') : t('registration.step3.selectPlan')}
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex justify-between pt-5" style={{ borderTop: "1px solid #23252a" }}>
          <button type="button" onClick={onPrevious} disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: "var(--lm-s2)", color: "var(--lm-ink)", border: "1px solid #23252a" }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = "var(--lm-s3)")} onMouseLeave={e => (e.currentTarget.style.backgroundColor = "var(--lm-s2)")}>
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
            {t('common.back')}
          </button>
          <button type="submit" disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: "#5e6ad2", color: "#fff", opacity: loading ? 0.5 : 1 }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = "#828fff"; }} onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#5e6ad2")}>
            {t('common.continue')}
            <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default Step3PlanSelection;
